import { useMemo, useState } from 'react';
import { useBrokerSymbols, useLeverageClients, useSaveOperation } from '@/hooks/leverageQueries';
import { useToast } from '@/hooks/useToast';
import { Field, Modal } from '@/components/ui';
import { IconCheck } from '@/components/Icons';
import { formatBRL, nowHHMM, todayISO } from '@/lib/format';
import {
  bpToInput, DIRECTION_LABELS, FULL_BP, formatBp, inputToBp, suggestedPercent,
} from '@/lib/leverage';
import type { LeverageDirection, LeverageOperation, LeverageTier } from '@/lib/types';
import { ClientAllocationList, shareAmount, type ClientRow, type Pick } from './ClientAllocationList';
import { newTierKey, TiersEditor, type TierDraft } from './TiersEditor';
import { SymbolPicker } from './SymbolPicker';

interface OperationModalProps {
  /** Com ela, edita; sem ela, monta uma operação nova. */
  operation?: LeverageOperation;
  onClose: () => void;
}

const DIRECTIONS: LeverageDirection[] = ['BUY', 'SELL'];

const toTiers = (drafts: readonly TierDraft[]): LeverageTier[] =>
  drafts.map((draft) => ({ minCapital: draft.minCapital, percentBp: inputToBp(draft.percent) }));

/** Os clientes do modal: os que têm capital e, na edição, os que já estão na operação. */
function useClientRows(operation: LeverageOperation | undefined): { rows: ClientRow[]; loading: boolean } {
  const { data, isLoading } = useLeverageClients();
  const rows = useMemo(() => {
    const own = new Map(operation?.status === 'OPEN' ? operation.allocations.map((item) => [item.lead.id, item]) : []);
    const byLead = new Map<string, ClientRow>();
    for (const client of data?.items ?? []) {
      const mine = own.get(client.lead.id);
      byLead.set(client.lead.id, {
        lead: client.lead,
        capital: client.capital,
        // A fatia que esta operação já tem não conta contra ela mesma.
        freeBp: FULL_BP - (client.allocatedBp ?? 0) + (mine?.percentBp ?? 0),
        executed: mine?.executedAt ? { percentBp: mine.percentBp, amount: mine.amount } : null,
        saved: mine ? { percentBp: mine.percentBp, amount: mine.amount } : null,
      });
    }
    for (const item of operation?.allocations ?? []) {
      if (byLead.has(item.lead.id)) continue;
      byLead.set(item.lead.id, {
        lead: item.lead,
        capital: 0,
        freeBp: FULL_BP,
        executed: item.executedAt ? { percentBp: item.percentBp, amount: item.amount } : null,
        saved: { percentBp: item.percentBp, amount: item.amount },
      });
    }
    return [...byLead.values()].sort((a, b) => a.lead.name.localeCompare(b.lead.name, 'pt-BR'));
  }, [data, operation]);
  return { rows, loading: isLoading };
}

/**
 * Monta (ou edita) uma operação: símbolo, direção, data e hora, as faixas de %
 * e os clientes que entram. O valor de cada cliente é o capital × %, e o
 * servidor confere de novo que ninguém passa de 100% do capital.
 */
export function OperationModal({ operation, onClose }: OperationModalProps) {
  const toast = useToast();
  const save = useSaveOperation();
  const { rows, loading } = useClientRows(operation);
  const brokerSymbols = useBrokerSymbols();

  const [symbol, setSymbol] = useState(operation?.symbol ?? '');
  const [direction, setDirection] = useState<LeverageDirection>(operation?.direction ?? 'BUY');
  const [date, setDate] = useState(operation?.date ?? todayISO());
  const [time, setTime] = useState(operation?.time ?? nowHHMM());
  const [expiry, setExpiry] = useState(String(operation?.expiryMinutes ?? 1));
  const [note, setNote] = useState(operation?.note ?? '');
  const [tiers, setTiers] = useState<TierDraft[]>(
    () => operation?.tiers.map((tier) => ({ key: newTierKey(), minCapital: tier.minCapital, percent: bpToInput(tier.percentBp) })) ?? [],
  );
  const [picks, setPicks] = useState<Record<string, Pick>>(() =>
    Object.fromEntries((operation?.allocations ?? []).map((item) => [item.lead.id, { percent: bpToInput(item.percentBp), manual: true }])),
  );
  const [error, setError] = useState<string | null>(null);

  const capitalOf = useMemo(() => new Map(rows.map((row) => [row.lead.id, row.capital])), [rows]);
  const suggestion = (leadId: string, drafts: readonly TierDraft[]) => {
    const bp = suggestedPercent(toTiers(drafts).filter((tier) => tier.percentBp > 0), capitalOf.get(leadId) ?? 0);
    return bp ? bpToInput(bp) : '';
  };

  /** Faixas mudaram: os clientes não ajustados à mão acompanham. */
  function changeTiers(next: TierDraft[]) {
    setTiers(next);
    setPicks((current) =>
      Object.fromEntries(Object.entries(current).map(([leadId, pick]) => [leadId, pick.manual ? pick : { ...pick, percent: suggestion(leadId, next) }])),
    );
  }

  function applyAll() {
    const executed = new Set(rows.filter((row) => row.executed).map((row) => row.lead.id));
    setPicks((current) =>
      Object.fromEntries(
        Object.entries(current).map(([leadId, pick]) => [leadId, executed.has(leadId) ? pick : { percent: suggestion(leadId, tiers), manual: false }]),
      ),
    );
  }

  function toggle(row: ClientRow) {
    setPicks((current) => {
      if (current[row.lead.id]) {
        const { [row.lead.id]: _removed, ...rest } = current;
        return rest;
      }
      return { ...current, [row.lead.id]: { percent: suggestion(row.lead.id, tiers), manual: false } };
    });
  }

  const picked = rows.filter((row) => picks[row.lead.id]);
  const shares = picked.map((row) => ({ row, bp: row.executed ? row.executed.percentBp : inputToBp(picks[row.lead.id]!.percent) }));
  const total = shares.reduce((sum, { row, bp }) => sum + shareAmount(row, bp), 0);

  function problem(): string | null {
    if (!symbol.trim()) return 'Informe o símbolo.';
    if (!date) return 'Informe a data da operação.';
    if (!/^\d+$/.test(expiry) || Number(expiry) < 1 || Number(expiry) > 1440) return 'A expiração vai de 1 a 1440 minutos.';
    const draftTiers = toTiers(tiers);
    if (draftTiers.some((tier) => tier.percentBp <= 0 || tier.percentBp > FULL_BP)) return 'Toda faixa precisa de um % entre 0 e 100.';
    if (new Set(draftTiers.map((tier) => tier.minCapital)).size !== draftTiers.length) return 'Duas faixas começam no mesmo capital.';
    if (shares.length === 0) return 'Escolha ao menos um cliente.';
    const empty = shares.find(({ bp }) => bp <= 0);
    if (empty) return `Informe o % de ${empty.row.lead.name}.`;
    const over = shares.find(({ row, bp }) => bp > row.freeBp);
    if (over) return `${over.row.lead.name} passaria de 100% do capital: só ${formatBp(Math.max(0, over.row.freeBp))} está livre.`;
    return null;
  }

  async function submit() {
    const found = problem();
    if (found) return setError(found);
    setError(null);
    try {
      await save.mutateAsync({
        ...(operation ? { id: operation.id } : {}),
        symbol: symbol.trim().toUpperCase(),
        direction,
        date,
        time,
        expiryMinutes: Number(expiry),
        tiers: toTiers(tiers),
        note: note.trim() || (operation ? null : undefined),
        allocations: shares.map(({ row, bp }) => ({ leadId: row.lead.id, percentBp: bp })),
      });
      toast.success(operation ? 'Operação salva.' : `Operação ${symbol.trim().toUpperCase()} montada com ${shares.length} ${shares.length === 1 ? 'cliente' : 'clientes'}.`);
      onClose();
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  const closed = operation?.status === 'CLOSED';
  const asset = brokerSymbols.data?.items.find((item) => item.symbol === symbol.trim().toUpperCase());
  const allowedExpiry = asset?.expiryMinutes ?? [];
  const expiryHint =
    allowedExpiry.length === 0
      ? undefined
      : allowedExpiry.includes(Number(expiry))
        ? `Aceitas: ${allowedExpiry.join(', ')}`
        : `Use ${allowedExpiry.slice(0, -1).join(', ')} ou ${allowedExpiry.at(-1)}`;

  return (
    <Modal
      title={operation ? `Editar ${operation.symbol}` : 'Novo sinal'}
      width={880}
      onClose={onClose}
      footer={
        <>
          <span className="lev-foot-total muted">
            {shares.length} {shares.length === 1 ? 'cliente' : 'clientes'} · <strong className="num">{formatBRL(total)}</strong> a entrar
          </span>
          <div className="spacer" />
          <button type="button" className="btn" onClick={onClose} disabled={save.isPending}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={save.isPending || closed}>
            {save.isPending ? <span className="spinner" /> : <IconCheck />} {operation ? 'Salvar' : 'Criar sinal'}
          </button>
        </>
      }
    >
      {closed ? <div className="alert alert-info" role="note">Operação encerrada: reabra para mudar.</div> : null}
      {asset?.marketClosed ? (
        <div className="alert alert-info" role="note">O mercado de {asset.symbol} está fechado agora na corretora.</div>
      ) : null}

      <div className="lev-op-fields">
        <div className="field">
          <label className="field-label" htmlFor="lev-simbolo">Símbolo *</label>
          <SymbolPicker
            inputId="lev-simbolo"
            value={symbol}
            onChange={setSymbol}
            symbols={brokerSymbols.data?.items ?? []}
            loading={brokerSymbols.isLoading}
            unavailable={
              brokerSymbols.error
                ? 'Lista da corretora indisponível: digite o símbolo.'
                : brokerSymbols.data && !brokerSymbols.data.slot
                  ? 'Configure o slot em Sinais para ver os ativos.'
                  : null
            }
            autoFocus={!operation}
          />
        </div>
        <div className="field">
          <span className="field-label" id="lev-direcao">Direção *</span>
          <div className="lev-seg" role="group" aria-labelledby="lev-direcao">
            {DIRECTIONS.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={direction === option}
                className="lev-seg-btn"
                data-direction={option}
                onClick={() => setDirection(option)}
              >
                {DIRECTION_LABELS[option]}
              </button>
            ))}
          </div>
        </div>
        <Field label="Data *">
          <input className="inp" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </Field>
        <Field label="Hora *">
          <input className="inp" type="time" value={time} onChange={(event) => setTime(event.target.value)} />
        </Field>
        <Field label="Expira (min) *" hint={expiryHint}>
          <input
            className="inp num"
            type="number"
            inputMode="numeric"
            min={1}
            max={1440}
            step={1}
            value={expiry}
            onChange={(event) => setExpiry(event.target.value)}
          />
        </Field>
      </div>

      <TiersEditor tiers={tiers} onChange={changeTiers} onApplyAll={applyAll} canApply={picked.length > 0} />

      {loading ? (
        <div className="skeleton" style={{ height: 160 }} />
      ) : (
        <ClientAllocationList
          rows={rows}
          picks={picks}
          onToggle={toggle}
          onPercent={(leadId, percent) => setPicks((current) => ({ ...current, [leadId]: { percent, manual: true } }))}
        />
      )}

      <Field label="Observação">
        <input className="inp" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Opcional" maxLength={500} />
      </Field>

      {error ? <div className="alert alert-error" role="alert">{error}</div> : null}
    </Modal>
  );
}
